import clsx from "clsx";

import type { EntryStatus } from "../lib/types";
import { PhotoMount } from "./PhotoMount";

export type DevelopingPhase =
  | "waiting"
  | "presigning"
  | "uploading"
  | "finalising"
  | "error"
  | EntryStatus;

export type DevelopingCardProps = {
  /** Entry id when we have one, otherwise the local upload id. Seeds the mount. */
  seedId: string;
  /** A local object URL while the real photograph is still in the post. */
  previewUrl: string | null;
  phase: DevelopingPhase;
  /** 0..1 during the PUT; null once the bytes are gone and the worker has it. */
  progress?: number | null;
  filename?: string;
  error?: string | null;
  onCancel?: () => void;
  onRetry?: () => void;
  onDismiss?: () => void;
  className?: string;
};

/**
 * A print in the developer tray.
 *
 * The signature interaction: your photograph appears instantly, pale and
 * desaturated under a drifting chemical sheen, with the specimen name still a
 * blank ruled line — a herbarium sheet waiting to be filled in. It resolves to
 * full colour only when Claude has actually named the thing.
 */
export function DevelopingCard({
  seedId,
  previewUrl,
  phase,
  progress = null,
  filename,
  error,
  onCancel,
  onRetry,
  onDismiss,
  className,
}: DevelopingCardProps) {
  const wording = describePhase(phase);
  const showProgress = progress !== null && phase === "uploading";
  const percent = Math.round((progress ?? 0) * 100);
  const hasError = typeof error === "string" && error.length > 0;

  return (
    <article
      className={clsx("sheet grain relative animate-rise overflow-hidden p-3 pb-4", className)}
      aria-busy={!hasError}
    >
      <PhotoMount
        seedId={seedId}
        src={previewUrl}
        alt={filename ? `Uploading ${filename}` : "A photograph being developed"}
        developing={!hasError}
        muted={hasError}
        eager
      >
        {showProgress ? (
          <div className="absolute inset-x-0 bottom-0 h-1.5 bg-[color-mix(in_oklab,var(--color-ink)_18%,transparent)]">
            <div
              className="h-full bg-ochre transition-[width] duration-300 ease-out"
              style={{ width: `${percent}%` }}
            />
          </div>
        ) : null}
      </PhotoMount>

      <div className="mt-5 px-1">
        {/* The name hasn't arrived yet, so the line is ruled and empty. */}
        <p className="field-label">{hasError ? "Not sent" : "Specimen"}</p>
        <div className="ruled mt-2 w-3/4" role="presentation" />

        <p
          role="status"
          aria-live="polite"
          className={clsx(
            "mt-3 font-prose text-[0.9375rem] leading-snug",
            hasError ? "text-rust" : "text-ink-soft",
          )}
        >
          {hasError ? error : wording.line}
          {showProgress ? <span className="ml-1.5 font-label text-xs tracking-wide opacity-70">{percent}%</span> : null}
        </p>

        {filename ? (
          <p className="mt-1 truncate font-label text-xs tracking-wide text-ink-soft opacity-60">
            {filename}
          </p>
        ) : null}

        {(onCancel || onRetry || onDismiss) && (
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
            {hasError && onRetry ? (
              <button type="button" onClick={onRetry} className="link touch font-label text-sm">
                Try that again
              </button>
            ) : null}
            {hasError && onDismiss ? (
              <button
                type="button"
                onClick={onDismiss}
                className="touch font-label text-sm text-ink-soft underline decoration-rule underline-offset-4 hover:text-ink"
              >
                Set it aside
              </button>
            ) : null}
            {!hasError && onCancel ? (
              <button
                type="button"
                onClick={onCancel}
                className="touch font-label text-sm text-ink-soft underline decoration-rule underline-offset-4 hover:text-ink"
              >
                Cancel
              </button>
            ) : null}
          </div>
        )}
      </div>
    </article>
  );
}

type PhaseWording = { line: string };

/** What to say while the user waits. Honest about which stage we're at. */
export function describePhase(phase: DevelopingPhase): PhaseWording {
  switch (phase) {
    case "waiting":
      return { line: "Waiting its turn to be sent." };
    case "presigning":
      return { line: "Finding a page for this one…" };
    case "uploading":
    case "pending_upload":
      return { line: "Sending the photograph…" };
    case "finalising":
      return { line: "Handing it to the darkroom…" };
    case "queued":
      return { line: "In the queue. Claude will look at it shortly." };
    case "developing":
      return { line: "Developing. This usually takes a few seconds." };
    case "complete":
      return { line: "Mounting the page…" };
    case "rejected":
      return { line: "Claude couldn't make this one out." };
    case "failed":
      return { line: "Something went wrong in the darkroom." };
    case "error":
      return { line: "That one didn't send." };
    default:
      return { line: "Working on it…" };
  }
}
